import { cs } from '../../lib/currency';
import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  CheckCircle, Clock, CreditCard, Users, ArrowUpDown, ArrowUp, ArrowDown,
  ExternalLink, AlertCircle,
} from 'lucide-react';

// Sortable Registrations Table Component (parent-facing)
export const RegistrationsTable = ({ registrations, tournaments, onPayment, onCancelRegistration }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'tournament_name', direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  // Player Status: about registration journey (not payment)
  const getPlayerStatus = (reg) => {
    if (reg.status === 'cancelled') return 'cancelled';
    if (reg.withdrawal_requested) return 'withdrawal_pending';
    if (reg.team_name) return 'selected';
    return 'pending';
  };

  // Payment Status: about money only (3 values)
  const getPaymentStatus = (reg) => {
    if (reg.payment_status === 'paid' || reg.status === 'confirmed') return 'paid';
    if (reg.payment_status === 'pending' || reg.status === 'payment_pending') return 'pending';
    return 'unpaid';
  };

  const sortedRegistrations = [...registrations].sort((a, b) => {
    const tournamentA = tournaments.find(t => t.id === a.tournament_id);
    const tournamentB = tournaments.find(t => t.id === b.tournament_id);

    let valueA, valueB;

    switch (sortConfig.key) {
      case 'tournament_name':
        valueA = (a.tournament_name || tournamentA?.name || '').toLowerCase();
        valueB = (b.tournament_name || tournamentB?.name || '').toLowerCase();
        break;
      case 'player_name':
        valueA = (a.player_name || '').toLowerCase();
        valueB = (b.player_name || '').toLowerCase();
        break;
      case 'age_category':
        valueA = (a.age_category || '').toLowerCase();
        valueB = (b.age_category || '').toLowerCase();
        break;
      case 'team_name':
        valueA = (a.team_name || '').toLowerCase();
        valueB = (b.team_name || '').toLowerCase();
        break;
      case 'start_date':
        valueA = a.tournament_start_date || tournamentA?.start_date || '';
        valueB = b.tournament_start_date || tournamentB?.start_date || '';
        break;
      case 'status':
        valueA = getPlayerStatus(a);
        valueB = getPlayerStatus(b);
        break;
      case 'payment':
        valueA = getPaymentStatus(a);
        valueB = getPaymentStatus(b);
        break;
      default:
        return 0;
    }

    if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortableHeader = ({ label, sortKey }) => (
    <TableHead
      className="cursor-pointer hover:bg-slate-50 select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center">
        {label}
        {getSortIcon(sortKey)}
      </div>
    </TableHead>
  );

  const PlayerStatusBadge = ({ reg }) => {
    const playerStatus = getPlayerStatus(reg);
    switch (playerStatus) {
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 text-xs">Cancelled</Badge>;
      case 'withdrawal_pending':
        return <Badge className="bg-amber-100 text-amber-700 text-xs">Withdrawal Pending</Badge>;
      case 'selected':
        return (
          <Badge className="bg-blue-100 text-blue-700 text-xs">
            <Users className="h-3 w-3 mr-1" />{reg.team_name}
          </Badge>
        );
      case 'pending':
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Pending</Badge>;
    }
  };

  const PaymentBadge = ({ reg, tournament }) => {
    const paymentStatus = getPaymentStatus(reg);
    if (reg.status === 'cancelled') return null;
    switch (paymentStatus) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>;
      case 'pending':
        return <Badge className="bg-orange-100 text-orange-700 text-xs"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'unpaid':
      default:
        return tournament && tournament.initial_payment_amount > 0
          ? <Badge className="bg-slate-100 text-slate-700 text-xs"><AlertCircle className="h-3 w-3 mr-1" />Unpaid</Badge>
          : <Badge className="bg-slate-100 text-slate-600 text-xs">Free</Badge>;
    }
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader label="Tournament" sortKey="tournament_name" />
              <SortableHeader label="Child" sortKey="player_name" />
              <SortableHeader label="Category" sortKey="age_category" />
              <SortableHeader label="Dates" sortKey="start_date" />
              <SortableHeader label="Status" sortKey="status" />
              <SortableHeader label="Payment" sortKey="payment" />
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRegistrations.map(reg => {
              const tournament = tournaments.find(t => t.id === reg.tournament_id);
              const startDate = reg.tournament_start_date || tournament?.start_date;
              const endDate = reg.tournament_end_date || tournament?.end_date;
              const playerStatus = getPlayerStatus(reg);
              const paymentStatus = getPaymentStatus(reg);

              return (
                <TableRow key={reg.id} data-testid={`registration-row-${reg.id}`}>
                  <TableCell className="font-medium">
                    {reg.tournament_name || tournament?.name}
                  </TableCell>
                  <TableCell>{reg.player_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase">
                      {reg.age_category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600 whitespace-nowrap text-sm">
                    {startDate ? new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                    {endDate ? ` — ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <PlayerStatusBadge reg={reg} />
                      {reg.cricclubs_url && (
                        <a href={reg.cricclubs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800" data-testid={`cricclubs-link-${reg.id}`}>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <PaymentBadge reg={reg} tournament={tournament} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {paymentStatus === 'unpaid' && playerStatus !== 'cancelled' && tournament && tournament.initial_payment_amount > 0 && (
                        <Button size="sm" onClick={() => onPayment(tournament, reg)} className="bg-green-600 hover:bg-green-700" data-testid={`pay-${reg.id}`}>
                          <CreditCard className="h-3 w-3 mr-1" />Pay {cs()}{tournament?.initial_payment_amount}
                        </Button>
                      )}
                      {playerStatus !== 'cancelled' && !reg.withdrawal_requested && (
                        <Button variant="outline" size="sm" onClick={() => onCancelRegistration(reg.tournament_id, reg.id, reg)} className="text-red-600 hover:text-red-700" data-testid={`withdraw-${reg.id}`}>
                          Withdraw
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {sortedRegistrations.map(reg => {
          const tournament = tournaments.find(t => t.id === reg.tournament_id);
          const startDate = reg.tournament_start_date || tournament?.start_date;
          const playerStatus = getPlayerStatus(reg);
          const paymentStatus = getPaymentStatus(reg);

          return (
            <div key={`m-${reg.id}`} className="border rounded-lg p-3 bg-white" data-testid={`registration-mobile-${reg.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{reg.tournament_name || tournament?.name}</p>
                  <p className="text-xs text-slate-500">{reg.player_name}</p>
                </div>
                <PaymentBadge reg={reg} tournament={tournament} />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <Badge variant="outline" className="uppercase text-[10px]">{reg.age_category}</Badge>
                <PlayerStatusBadge reg={reg} />
                {startDate && (
                  <span className="text-xs text-slate-500">
                    {new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
                {reg.cricclubs_url && (
                  <a href={reg.cricclubs_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-0.5">
                    <ExternalLink className="h-3 w-3" />Schedule
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                {paymentStatus === 'unpaid' && playerStatus !== 'cancelled' && tournament && tournament.initial_payment_amount > 0 && (
                  <Button size="sm" onClick={() => onPayment(tournament, reg)} className="bg-green-600 hover:bg-green-700 h-7 text-xs flex-1">
                    <CreditCard className="h-3 w-3 mr-1" />Pay {cs()}{tournament.initial_payment_amount}
                  </Button>
                )}
                {playerStatus !== 'cancelled' && !reg.withdrawal_requested && (
                  <Button variant="outline" size="sm" onClick={() => onCancelRegistration(reg.tournament_id, reg.id, reg)} className="text-red-600 h-7 text-xs">Withdraw</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default RegistrationsTable;
